/**
 * Canvas Drawing Tool
 */
export default class CanvasDraw {
	/**
	 * Class constructor
	 *
	 * @param {CanvasClipArea} clipArea
	 */
	constructor(clipArea) {
		/**
		 * @private
		 * @type {CanvasClipArea}
		 */
		this._clipArea = clipArea;

		/**
		 * @type {T_Rectangle}
		 * @private
		 */
		this._lastRect = {
			left: 0,
			top: 0,
			right: 0,
			bottom: 0
		};

		/**
		 * @type {{
		 * path: Array<{T_Point}>, lastX: number, lastY: number, baseX: number, baseY: number,
		 * xCoef: number, yCoef: number, isJustDumped: boolean, isOneLine: boolean,
		 * minX: number, maxX:number, minY: number, maxY: number
		 * }}
		 * @private
		 */
		this._path = {
			path: [],
			isOneLine: false,
			isJustDumped: false,
			lastX: 0,
			lastY: 0,
			baseX: 0,
			baseY: 0,
			minX: 0,
			minY: 0,
			maxX: 0,
			maxY: 0,
			xCoef: 0,
			yCoef: 0,
		};
	}

	/**
	 * Returns changed area bounding rectangle
	 *
	 * @returns {T_Rectangle}
	 */
	get lastDrawnRect() {
		return this._lastRect;
	}

	/**
	 * Updates last brush X and Y coordinates
	 *
	 * @param {T_Point} p
	 * @returns {CanvasDraw}
	 */
	updateLastXY(p) {
		this._path.lastX = p.x;
		this._path.lastY = p.y;
		return this;
	}

	/**
	 * Saves draw path state
	 *
	 * @param {T_State} rec
	 * @returns {CanvasDraw}
	 */
	saveState(rec) {
		let path = this._path;
		rec.path = {
			lastX: path.lastX,
			lastY: path.lastY,
		};
		return this;
	}

	/**
	 * Restores draw path state
	 *
	 * @param {T_State} rec
	 * @returns {CanvasDraw}
	 */
	restoreState(rec) {
		let path = this._path,
			state = rec.path;
		path.lastX = state.lastX;
		path.lastY = state.lastY;
		return this;
	}

	/**
	 * Initializes draw new line
	 *
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {T_Point} pos
	 * @param {Color} color
	 * @param {number} [brushSize]
	 * @param {boolean} [isShift]
	 * @returns {void}
	 */
	initNewLine(ctx, pos, color, brushSize = 1, isShift = false) {
		let rect = this._lastRect;
		rect.right = rect.left = pos.x;
		rect.top = rect.bottom = pos.y;
		this._path.xCoef = 0;
		this._path.yCoef = 0;
		this._path.isJustDumped = false;

		if (isShift && (this._path.lastX !== 0 || this._path.lastY !== 0)) {
			// draw line between last point and current one
			this._path.path = [
				{x: this._path.lastX, y: this._path.lastY}
			];
			// update redraw rectangle
			rect.left = Math.min(rect.left, this._path.lastX);
			rect.top = Math.min(rect.top, this._path.lastY);
			rect.right = Math.max(rect.right, this._path.lastX);
			rect.bottom = Math.max(rect.bottom, this._path.lastY);
			this._path.isOneLine = true;
		} else {
			this._path.isOneLine = false;
			this._path.path = [
				{x: pos.x, y: pos.y}
			];
		}

		this._path.lastX = pos.x;
		this._path.lastY = pos.y;
		ctx.lineCap = 'round';
		ctx.lineJoin = 'round';
		ctx.strokeStyle = color.rgbCss;
		this.draw(ctx, pos, brushSize, isShift);
	}

	/**
	 * Draws line using the current brush
	 *
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {T_Point} pos
	 * @param {number} [brushSize]
	 * @param {boolean} [isShift]
	 * @returns {void}
	 */
	draw(ctx, pos, brushSize = 1, isShift = false) {
		let drawX, drawY, point,
			rect = this._lastRect,
			path = this._path,
			points = path.path;

		drawX = pos.x;
		drawY = pos.y;

		if (!path.isOneLine && isShift && (points.length > 1 || path.isJustDumped)) {
			// calculate strict direction line drawing parameters
			let i = points.length - 1, point = points[i];
			while (point === null) {
				point = points[--i];
			}
			if (path.xCoef === 0 && path.yCoef === 0) {
				// the direction is undefined yet
				let dx = point.x - drawX,
					dy = point.y - drawY,
					d = Math.sqrt(dx * dx + dy * dy);
				if (d < 3) {
					// the distance if too small, wait next point
					return;
				}
				// remember start point, we will draw based on it
				path.minX = path.maxX = path.baseX = point.x;
				path.minY = path.maxY = path.baseY = point.y;

				// null - is start new line
				points.push(null);

				// calculate normalized vector
				dx /= d;
				dy /= d;

				// define direction coefficients
				if (Math.abs(dx) >= Math.abs(dy * 4)) {
					path.xCoef = 1;
					path.yCoef = 0;
				} else if (Math.abs(dy) >= Math.abs(dx * 4)) {
					path.xCoef = 0;
					path.yCoef = 1;
				} else {
					let {nx, ny} = this._clipArea.getProperties();
					path.xCoef = nx;
					path.yCoef = Math.sign(dx) !== Math.sign(dy) ? -ny : ny;
				}
			}
			// remove all points after line break
			while (points[points.length - 1] !== null) {
				points.pop();
			}
			// correct current draw point with previously defined direction
			if (path.xCoef === 0) {
				// vertical line
				drawX = path.baseX;
				drawY = path.baseY + (drawY - path.baseY) * path.yCoef;
				path.minY = Math.min(drawY, path.minY);
				path.maxY = Math.max(drawY, path.maxY);
				drawY = path.maxY;
				points.push({x: drawX, y: path.minY});
				rect.top = Math.min(rect.top, path.minY - brushSize);
				rect.bottom = Math.max(rect.bottom, path.minY + brushSize);
			} else if (path.yCoef === 0) {
				// horizontal line
				drawX = path.baseX + (drawX - path.baseX) * path.xCoef;
				drawY = path.baseY;
				path.minX = Math.min(drawX, path.minX);
				path.maxX = Math.max(drawX, path.maxX);
				drawX = path.maxX;
				points.push({x: path.minX, y: drawY});
				rect.left = Math.min(rect.left, path.minX - brushSize);
				rect.right = Math.max(rect.right, path.minX + brushSize);
			} else {
				let dx = drawX - path.baseX,
					dy = drawY - path.baseY,
					l = Math.sqrt(dx * dx + dy * dy);
				drawX = path.baseX + l * path.xCoef * Math.sign(dx);
				drawY = path.baseY + l * path.yCoef * Math.sign(dx);
				path.minX = Math.min(drawX, path.minX);
				path.maxX = Math.max(drawX, path.maxX);
				path.minY = Math.min(drawY, path.minY);
				path.maxY = Math.max(drawY, path.maxY);
				if (path.yCoef < 0) {
					points.push({x: path.minX, y: path.maxY});
					rect.top = Math.min(rect.top, path.maxY - brushSize);
					rect.bottom = Math.max(rect.bottom, path.maxY + brushSize);
					drawY = path.minY;
				} else {
					points.push({x: path.minX, y: path.minY});
					rect.top = Math.min(rect.top, path.minY - brushSize);
					rect.bottom = Math.max(rect.bottom, path.minY + brushSize);
					drawY = path.maxY;
				}
				drawX = path.maxX;
				rect.left = Math.min(rect.left, path.minX - brushSize);
				rect.right = Math.max(rect.right, path.minX + brushSize);
			}
		} else if (!isShift) {
			if (path.xCoef !== 0 || path.yCoef !== 0) {
				// reset draw by direction
				path.xCoef = 0;
				path.yCoef = 0;
				// null - is start new line
				points.push(null);
			}
		}

		if (path.isOneLine) {
			// remove all points, except first, because only one line is required
			points.splice(1);
		}

		points.push({x: drawX, y: drawY});
		path.isJustDumped = false;

		ctx.lineWidth = brushSize;
		ctx.beginPath();

		let isNewPath = true;
		for (let i = 0, l = points.length; i < l; i++) {
			point = points[i];
			if (point === null) {
				isNewPath = true;
			} else if (isNewPath) {
				isNewPath = false;
				ctx.moveTo(point.x, point.y);
			} else {
				ctx.lineTo(point.x, point.y);
			}
		}
		ctx.stroke();
		path.lastX = drawX;
		path.lastY = drawY;

		rect.left = Math.min(rect.left, drawX - brushSize);
		rect.top = Math.min(rect.top, drawY - brushSize);
		rect.right = Math.max(rect.right, drawX + brushSize);
		rect.bottom = Math.max(rect.bottom, drawY + brushSize);
	}

	/**
	 * Returns TRUE if there are too many points in buffer
	 *
	 * @returns {boolean}
	 */
	isBufferOverflow() {
		return this._path.path.length > 40;
	}

	/**
	 * Clears draw buffer
	 *
	 * @returns {void}
	 */
	clearBuffer() {
		if (this._path.path.length > 0) {
			this._path.path.splice(0, this._path.path.length - 1);
		}
		this._path.isJustDumped = true;
	}

	/**
	 * Finishes line draw and returns changed area bounding rectangle
	 *
	 * @returns {T_Rectangle}
	 */
	finish() {
		this._path.path = [];
		this._path.xCoef = 0;
		this._path.yCoef = 0;
		this._path.isOneLine = false;
		return this._lastRect;
	}

	/**
	 * Draws highlight (trace) of the list to the next point.
	 * This works for Brush, Erase, Lasso tools if press SHIFT and move cursor over the scene.
	 *
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {number} toX
	 * @param {number} toY
	 * @param {number} [offsetX]
	 * @param {number} [offsetY]
	 * @param {number} [scaleFactor]
	 * @returns {void}
	 */
	drawTraceFromLastXY(ctx, toX, toY, offsetX = 0, offsetY = 0, scaleFactor = 1) {
		if (this._path.path.length > 0) {
			return;
		}
		let path = this._path;
		ctx.save();
		ctx.globalCompositeOperation = 'difference';
		ctx.beginPath();
		ctx.strokeStyle = 'rgba(255, 255, 255, 1)';
		ctx.moveTo((path.lastX - offsetX) * scaleFactor, (path.lastY - offsetY) * scaleFactor);
		ctx.lineTo((toX - offsetX) * scaleFactor, (toY - offsetY) * scaleFactor);
		ctx.stroke();
		ctx.restore();
	}
}